import { defineStore } from "pinia";

import type { IPlaylistItem, ITelestration } from "~/types";

import type { VideoJsPlayer as IPixellotPlayer } from "@pixellot/web-sdk";

import { base64UrlToSVGString } from "../helpers/base64UrlToSVGString";
import { getShapesFromSVGString } from "../helpers/getShapesFromSVGString";

export enum TelestrationMode {
  PREVIEW = "preview",
  EDIT = "edit",
  NONE = "none",
}

export const useTelestrationStore = defineStore("telestration", () => {
  const shapesStore = useShapesStore();
  const currentPlayerInstance = ref<IPixellotPlayer | null>(null);
  const currentPlaylistItem = ref<IPlaylistItem | null>(null);
  const currentTelestration = ref<ITelestration | null>(null);
  const currentVideoTime = ref(0);
  const telestrationMode = ref<TelestrationMode>(TelestrationMode.NONE);
  const isNavigationToAnotherTelestration = ref(false);
  const isPreviewingTelestration = ref(false);
  const previewQueue = ref<ITelestration[]>([]);
  const telestrationButtonShown = ref<boolean>(isFeatureEnabled("enable_telestration_mode"));
  const isFullscreen = ref(!!document.fullscreenElement);
  const currentTelestrations = computed(() => {
    if (!currentPlaylistItem.value) return [];
    return currentPlaylistItem.value.telestrations.sort((a, b) => {
      return Number(a.start) - Number(b.start);
    });
  });
  const currentTelestrationIndex = computed(() => currentTelestrations.value.findIndex((telestration) => telestration.id === currentTelestration.value?.id));
  const nextTelestration = computed(() => currentTelestrations.value[currentTelestrationIndex.value + 1]);
  const previousTelestration = computed(() => currentTelestrations.value[currentTelestrationIndex.value - 1]);

  async function openPreviewForPlaylistItem(item: IPlaylistItem, currentTime: number, player: IPixellotPlayer | null, telestrationItem: ITelestration) {
    // If we are in edit mode, we don't want to open the preview
    if (telestrationMode.value === TelestrationMode.EDIT) {
      return;
    }
    if (isNavigationToAnotherTelestration.value) {
      return;
    }
    if (isPreviewingTelestration.value) {
      previewQueue.value.push(telestrationItem);
      return;
    }

    isPreviewingTelestration.value = true;

    const result = await enterTelestrationModeForPlaylistItem(TelestrationMode.PREVIEW, item, currentTime, player, telestrationItem);

    player?.one("play", onQueueNextTelestration);

    function onQueueNextTelestration() {
      const nextItem = previewQueue.value.shift();

      if (!player) {
        return;
      }

      if (nextItem) {
        // This is either seconds or Infinity
        const currentAutoPauseValue = player.telestrationAPI.getCurrentAutoPauseSetting();

        if (currentAutoPauseValue === 0) {
          onExitTelestrationMode();
          return;
        }

        if (currentAutoPauseValue === Infinity) {
          player.pause();

          openPreviewForPlaylistItem(item, currentTime, player, nextItem);

          player.one("play", () => onQueueNextTelestration());
          player.one("seeking", () => onExitTelestrationMode());
          return;
        }

        const pauseDelay = currentAutoPauseValue * 1000;

        player.pause();

        openPreviewForPlaylistItem(item, currentTime, player, nextItem);

        player.one("play", () => onQueueNextTelestration());
        player.one("seeking", () => onExitTelestrationMode());
        setTimeout(() => {
          if (telestrationMode.value !== TelestrationMode.EDIT) {
            player.play();
          }
        }, pauseDelay);
      } else {
        onExitTelestrationMode();
      }
    }

    isPreviewingTelestration.value = false;

    return result;
  }

  function openEditForPlaylistItem(item: IPlaylistItem, currentTime: number, player: IPixellotPlayer | null, telestrationItem?: ITelestration) {
    if (isNavigationToAnotherTelestration.value) return;

    if (telestrationMode.value === TelestrationMode.PREVIEW && currentTelestration.value) {
      return enterTelestrationModeForPlaylistItem(TelestrationMode.EDIT, item, currentTime, player, currentTelestration.value);
    }

    if (!telestrationItem) {
      console.warn("telestrationItem was not provided during opening of edit mode");
      return;
    }

    return enterTelestrationModeForPlaylistItem(TelestrationMode.EDIT, item, currentTime, player, telestrationItem);
  }

  async function enterTelestrationModeForPlaylistItem(
    mode: TelestrationMode,
    item: IPlaylistItem,
    currentTime: number,
    player: IPixellotPlayer | null,
    telestrationItem: ITelestration
  ) {
    resetStore();

    const time = currentTime ?? item.startTime;

    await nextTick();

    telestrationMode.value = mode;
    currentVideoTime.value = time;
    currentPlaylistItem.value = item;
    currentPlayerInstance.value = player;
    currentTelestration.value = telestrationItem;

    renderTelestrationsAsShapes(telestrationItem);
  }

  function openEditorForPlaylistItem(item: IPlaylistItem, currentTime: number, player: IPixellotPlayer | null) {
    resetStore();

    currentVideoTime.value = currentTime;
    currentPlaylistItem.value = item;
    currentPlayerInstance.value = player;
    telestrationMode.value = TelestrationMode.EDIT;
  }

  function setupTelestrationCanvasOnContainer(container: HTMLDivElement) {
    useCanvasStore().containerRef = container;
  }

  function resetCurrentTelestration() {
    currentTelestration.value = null;
    shapesStore.$reset();
    useToolsStore().$reset();
  }

  function resetStore() {
    resetCurrentTelestration();
    currentPlaylistItem.value = null;
    currentVideoTime.value = 0;
    telestrationMode.value = TelestrationMode.NONE;
  }

  function resetPreviewQueue() {
    previewQueue.value = [];
  }

  async function renderTelestrationsAsShapes(item: ITelestration) {
    resetCurrentTelestration();

    currentTelestration.value = item;

    await nextTick();

    shapesStore.historyState.pause();

    const decodedSvg = base64UrlToSVGString(item.body);

    if (!decodedSvg) return;

    const shapes = getShapesFromSVGString(decodedSvg);

    shapes.forEach((shape) => {
      shapesStore.addShape(shape);
    });

    useToolsStore().resetToSelectionTool();

    await nextTick();

    shapesStore.historyState.resume();

    shapesStore.historyState.clear();
  }

  async function openNextTelestration() {
    if (!nextTelestration.value || !nextTelestration.value?.start) return;

    isNavigationToAnotherTelestration.value = true;
    const nextTelestrationStartTime = nextTelestration.value.start - (currentPlaylistItem.value?.startTime ?? 0);
    currentVideoTime.value = nextTelestrationStartTime;

    currentPlayerInstance.value?.currentTime(nextTelestrationStartTime);
    await renderTelestrationsAsShapes(nextTelestration.value);
    setTimeout(() => {
      isNavigationToAnotherTelestration.value = false;
    }, 300);
  }

  async function openPreviousTelestration() {
    if (!previousTelestration.value || !previousTelestration.value.start) return;
    /*
      setTimeout is used to prevent entering PREVIEW mode after time intersection check
      300ms covers time intersection check of videojs player
    */
    isNavigationToAnotherTelestration.value = true;
    const previousTelestrationStartTime = previousTelestration.value.start - (currentPlaylistItem.value?.startTime ?? 0);
    currentVideoTime.value = previousTelestrationStartTime;

    currentPlayerInstance.value?.currentTime(previousTelestrationStartTime);
    await renderTelestrationsAsShapes(previousTelestration.value);
    setTimeout(() => {
      isNavigationToAnotherTelestration.value = false;
    }, 300);
  }

  function onExitTelestrationMode() {
    if (isNavigationToAnotherTelestration.value) return;

    resetStore();
  }

  function handleFullscreenChange(isFullscreenValue: boolean) {
    isFullscreen.value = isFullscreenValue;
  }

  function toggleFullscreenInTelestration() {
    if (isFullscreen.value) {
      currentPlayerInstance.value?.exitFullscreen();
    } else {
      currentPlayerInstance.value?.requestFullscreen();
    }
  }

  return {
    telestrationMode,
    telestrationButtonShown,
    currentVideoTime,
    currentPlaylistItem,
    currentTelestration,
    isFullscreen,
    resetPreviewQueue,
    openPreviewForPlaylistItem,
    openEditForPlaylistItem,
    openEditorForPlaylistItem,
    exitTelestrationMode: onExitTelestrationMode,
    setupTelestrationCanvasOnContainer,
    toggleFullscreenInTelestration,
    handleFullscreenChange,

    navigation: {
      canGoToNext: computed(() => !!nextTelestration.value),
      canGoToPrevious: computed(() => !!previousTelestration.value),
      next: openNextTelestration,
      previous: openPreviousTelestration,
    },
  };
});
